// Centralizes the naming scheme used when downloading generated assets.
//
// The agreed convention is `{slug}.{ext}` for single downloads and
// `{slug}_{N}.{ext}` for bulk downloads where N resets per group
// (per producto en el catálogo IA, per variante en el historial).
//
// El slug se deriva del nombre de la variante/producto: minúsculas,
// acentos removidos, todo lo no alfanumérico convertido a `_`.

const KNOWN_IMAGE_EXT_RE = /\.(png|jpe?g|webp|gif|bmp|tiff|svg)$/i;
const KNOWN_VIDEO_EXT_RE = /\.(mp4|webm|mov)$/i;

export const inferExtFromMime = (mime?: string): string => {
  if (!mime) return "";
  const m = mime.toLowerCase();
  if (m === "image/png") return "png";
  if (m === "image/jpeg") return "jpg";
  if (m === "image/webp") return "webp";
  if (m === "image/gif") return "gif";
  if (m === "image/bmp") return "bmp";
  if (m === "image/tiff") return "tiff";
  if (m === "image/svg+xml") return "svg";
  if (m === "video/mp4") return "mp4";
  if (m === "video/webm") return "webm";
  if (m === "video/quicktime") return "mov";
  return "";
};

const extFromOriginal = (fileName?: string): string => {
  if (!fileName) return "";
  const m =
    fileName.match(KNOWN_IMAGE_EXT_RE) || fileName.match(KNOWN_VIDEO_EXT_RE);
  return m ? m[0].slice(1).toLowerCase() : "";
};

/** Convierte un nombre arbitrario en un slug seguro para nombre de archivo:
 *  minúsculas, sin acentos, espacios y símbolos → `_`. Cae a `"imagen"` si
 *  no queda nada utilizable. */
export const slugifyVariantName = (name?: string | null): string => {
  const trimmed = (name || "").trim();
  if (!trimmed) return "imagen";
  const slug = trimmed
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_|_$/g, "");
  return slug || "imagen";
};

export interface BuildDownloadFileNameOpts {
  /** Nombre de variante/producto. Se slugifica internamente. */
  variantName?: string | null;
  /** Si está definido, se appendea como `_{index}`. Omitir para single
   *  downloads (`chaqueta_beige.png`). 1-based por convención. */
  index?: number;
  /** Mime del blob — usado como fallback para inferir extensión. */
  mime?: string;
  /** Nombre original del archivo en Storage — usado como fuente prioritaria
   *  para la extensión cuando existe. */
  originalFileName?: string;
  /** Fuerza `.mp4` (cuando sabemos que es video pero el mime/fileName no
   *  lo refleja, p.ej. thumbnails .png de un video). */
  forceVideo?: boolean;
}

/** Construye un filename canónico para descargas según la convención
 *  acordada. Ver header del archivo. */
export const buildDownloadFileName = (opts: BuildDownloadFileNameOpts): string => {
  // Si el variantName está vacío o es el literal genérico "Imagen"
  // (fallback que setea GeneratedImagesInline cuando la imagen no tiene
  // variante asociada), preferimos usar el stem del archivo original
  // como slug — así el download name no termina siendo "imagen.png" o
  // "imagen_1.png" para CUALQUIER imagen sin variante.
  const rawVariantName = (opts.variantName ?? "").trim();
  const isGenericVariantName =
    !rawVariantName || rawVariantName.toLowerCase() === "imagen";
  const slugSource = isGenericVariantName
    ? opts.originalFileName
      ? // Sacamos extensión y sufijos de numeración antes de slugificar
        // para que `aab1c2_4k.png` quede como `aab1c2_4k`.
        opts.originalFileName.replace(/\.[a-z0-9]{1,5}$/i, "") ||
        opts.originalFileName
      : "imagen"
    : rawVariantName;
  const slug = slugifyVariantName(slugSource);
  const indexSuffix =
    typeof opts.index === "number" && Number.isFinite(opts.index)
      ? `_${opts.index}`
      : "";
  if (opts.forceVideo) return `${slug}${indexSuffix}.mp4`;
  const ext =
    extFromOriginal(opts.originalFileName) ||
    inferExtFromMime(opts.mime) ||
    "png";
  return `${slug}${indexSuffix}.${ext}`;
};
